import type { Lang } from "../index";

export interface RecruitingScreeningCallCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: { heading: string; body: string; bullets: string[] };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: RecruitingScreeningCallCopy = {
  seo: {
    title: "Minuta for recruiters — screening calls without a bot in the room",
    description:
      "Recruiters can run screening calls with a live English transcript and AI-suggested follow-up questions, without a notetaker bot joining the meeting. Audio stays on the recruiter's Mac.",
  },
  eyebrow: "Use case · Recruiting",
  h1: "30-minute screen. 12 questions to ask. No bot in the room.",
  lede:
    "Recruiters live on screening calls — five a day, eight a day during a hiring sprint. Minuta gives you the live transcript and surfaces follow-up questions without a third-party bot joining the meeting.",
  problem: {
    heading: "Why bots in screening calls are awkward",
    body1:
      "Many candidates today have a clear opinion about meeting bots: \"Otter just joined,\" \"Fireflies is recording.\" In a 30-minute screen — where you're trying to build trust in the first 60 seconds — a bot in the participants list is a small but real friction.",
    body2:
      "Add to that the fact that some employers (and some candidates) prefer not to have call audio uploaded to a third-party SaaS. Recruiters end up taking notes by hand, missing follow-ups, and writing the summary from memory after the next call.",
  },
  scenarios: {
    heading: "Where it earns its keep",
    items: [
      {
        tag: "Live note-taking",
        title: "Five screens stacked back-to-back",
        body: "Minuta keeps a clean rolling transcript on the side. You scroll back to the answer about salary expectations or visa status without scrubbing through a recording later.",
      },
      {
        tag: "Follow-up prompts",
        title: "\"Tell me more about that team conflict\"",
        body: "When the candidate gives a short answer to a behavioral question, Minuta can suggest a STAR-format follow-up that fits the role brief you uploaded.",
      },
      {
        tag: "Structured summary",
        title: "Hand-off to the hiring manager",
        body: "After the call, Minuta produces a TL;DR, key signals, and red flags. Drop straight into the ATS or share with the hiring manager — no transcribing from a recording.",
      },
    ],
  },
  how: {
    heading: "How it fits a screening workflow",
    body:
      "Open Minuta a minute before the call. Attach the role brief and any candidate notes you have. Run the call as you normally would — Zoom, Teams, Google Meet, or a phone bridge.",
    bullets: [
      "No bot joins. The candidate sees no extra participant. Audio is captured locally on your computer.",
      "Live English transcript on the side, synchronized with the call.",
      "Follow-up suggestions surface in real time, anchored to the role brief.",
      "Post-call summary with TL;DR, role-fit signals, and explicit gaps to follow up on later.",
    ],
  },
  proof: {
    heading: "Why recruiters pick Minuta over generic notetakers",
    bullets: [
      "Bot-free screening calls are simpler to run with candidates who flag privacy concerns.",
      "Transcripts and notes for screening conversations stay in the recruiter's desktop app — exportable to the ATS on demand, deletable at any time.",
      "Suggested follow-up questions are grounded in the role brief, not generic templates.",
      "Structured summaries plug into the ATS hand-off without rework.",
    ],
  },
  cta: {
    heading: "Run a cleaner screen tomorrow",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: RecruitingScreeningCallCopy = {
  seo: {
    title: "Minuta pra recrutador — entrevista de screening sem bot na call",
    description:
      "Recrutador roda screening em inglês com transcrição ao vivo e sugestão de pergunta de follow-up — sem bot na call. O áudio fica no computador do recrutador.",
  },
  eyebrow: "Caso de uso · Recrutamento",
  h1: "Screening de 30 minutos. 12 perguntas. Sem bot na call.",
  lede:
    "Recrutador vive em screening — cinco por dia, oito num sprint de hiring. O Minuta entrega o transcript ao vivo e sugere o follow-up certo sem precisar de um notetaker entrando na call.",
  problem: {
    heading: "Por que bot em screening incomoda",
    body1:
      "Muito candidato hoje já tem opinião formada sobre bot em reunião: \"o Otter acabou de entrar\", \"o Fireflies está gravando\". Num screening de 30 minutos — onde a confiança se constrói nos primeiros 60 segundos — um bot na lista de participantes é uma fricção pequena, mas real.",
    body2:
      "Além disso, algumas empresas (e alguns candidatos) preferem que o áudio da call não seja subido pra um SaaS terceiro. O recrutador acaba anotando à mão, perdendo follow-up e escrevendo o resumo de memória depois da próxima call.",
  },
  scenarios: {
    heading: "Onde paga o investimento",
    items: [
      {
        tag: "Anotação ao vivo",
        title: "Cinco screens encadeados",
        body: "O Minuta mantém um transcript rolando do lado. Você volta no que o candidato falou sobre pretensão salarial ou visto sem ter que ir num replay depois.",
      },
      {
        tag: "Sugestão de follow-up",
        title: "\"Conta mais desse conflito de time\"",
        body: "Quando o candidato dá uma resposta curta numa pergunta comportamental, o Minuta sugere um follow-up no formato STAR puxando do brief da vaga que você anexou.",
      },
      {
        tag: "Resumo estruturado",
        title: "Hand-off pro hiring manager",
        body: "Depois da call, o Minuta entrega TL;DR, sinais positivos e pontos de atenção. Cola direto no ATS ou manda pro hiring manager — sem ter que transcrever de uma gravação.",
      },
    ],
  },
  how: {
    heading: "Como entra no fluxo de screening",
    body:
      "Abre o Minuta um minuto antes da call. Anexa o brief da vaga e qualquer nota sobre o candidato. Roda a call como sempre — Zoom, Teams, Google Meet ou ligação.",
    bullets: [
      "Nenhum bot entra. O candidato não vê participante a mais. O áudio é capturado local no seu computador.",
      "Transcript em inglês ao vivo do lado, sincronizado com a call.",
      "Sugestão de follow-up em tempo real, ancorada no brief da vaga.",
      "Resumo pós-call com TL;DR, sinais de role-fit e gaps explícitos pra retomar depois.",
    ],
  },
  proof: {
    heading: "Por que recrutador escolhe Minuta em vez de notetaker genérico",
    bullets: [
      "Screening sem bot é mais fácil de rodar com candidato que toca em privacidade.",
      "Áudio da conversa de screening não sai do computador do recrutador.",
      "Sugestão de follow-up vem do brief da vaga, não de template genérico.",
      "Resumos estruturados encaixam no hand-off do ATS sem retrabalho.",
    ],
  },
  cta: {
    heading: "Rode um screening mais limpo amanhã",
    body: "7 dias com tudo liberado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: RecruitingScreeningCallCopy = {
  seo: {
    title: "Minuta para recruiters — screenings sin bot en la call",
    description:
      "El recruiter corre el screening con transcripción en vivo y sugerencias de follow-up, sin bot en la call. El audio se queda en la computadora del recruiter.",
  },
  eyebrow: "Caso de uso · Reclutamiento",
  h1: "Screening de 30 minutos. 12 preguntas. Sin bot en la call.",
  lede:
    "El recruiter vive en screenings — cinco al día, ocho en un sprint de hiring. Minuta entrega el transcript en vivo y sugiere el follow-up correcto sin que un notetaker entre a la call.",
  problem: {
    heading: "Por qué un bot en un screening incomoda",
    body1:
      "Muchos candidatos hoy ya tienen una opinión formada sobre los bots: \"acaba de unirse Otter\", \"Fireflies está grabando\". En un screening de 30 minutos — donde la confianza se construye en los primeros 60 segundos — un bot en la lista de participantes es una fricción pequeña, pero real.",
    body2:
      "Además, algunas empresas (y algunos candidatos) prefieren que el audio de la call no se suba a un SaaS de terceros. El recruiter termina tomando notas a mano, perdiendo follow-ups y escribiendo el resumen de memoria después del siguiente screening.",
  },
  scenarios: {
    heading: "Dónde paga la inversión",
    items: [
      {
        tag: "Notas en vivo",
        title: "Cinco screens encadenados",
        body: "Minuta mantiene un transcript corriendo al lado. Vuelves a lo que el candidato dijo sobre salario esperado o visa sin tener que ir a un replay después.",
      },
      {
        tag: "Sugerencia de follow-up",
        title: "\"Cuéntame más sobre ese conflicto de equipo\"",
        body: "Cuando el candidato da una respuesta corta a una pregunta conductual, Minuta sugiere un follow-up en formato STAR usando el brief del rol que adjuntaste.",
      },
      {
        tag: "Resumen estructurado",
        title: "Hand-off al hiring manager",
        body: "Después de la call, Minuta entrega TL;DR, señales positivas y puntos de atención. Lo pegas directo en el ATS o se lo mandas al hiring manager — sin transcribir desde una grabación.",
      },
    ],
  },
  how: {
    heading: "Cómo encaja en un flujo de screening",
    body:
      "Abre Minuta un minuto antes de la call. Adjunta el brief del rol y cualquier nota sobre el candidato. Corre la call como siempre — Zoom, Teams, Google Meet o llamada.",
    bullets: [
      "Ningún bot entra. El candidato no ve un participante extra. El audio se captura local en tu computadora.",
      "Transcript en inglés en vivo al lado, sincronizado con la call.",
      "Sugerencia de follow-up en tiempo real, anclada al brief del rol.",
      "Resumen post-call con TL;DR, señales de role-fit y gaps explícitos para retomar.",
    ],
  },
  proof: {
    heading: "Por qué un recruiter elige Minuta en vez de un notetaker genérico",
    bullets: [
      "Screening sin bot es más fácil de correr con candidatos sensibles a la privacidad.",
      "El audio de la conversación de screening no sale de la computadora del recruiter.",
      "Las sugerencias de follow-up vienen del brief del rol, no de un template genérico.",
      "Los resúmenes estructurados encajan en el hand-off del ATS sin retrabajo.",
    ],
  },
  cta: {
    heading: "Corre un screening más limpio mañana",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const recruitingScreeningCallCopy: Record<Lang, RecruitingScreeningCallCopy> = { en, pt, es };
